import {
  AddNativeAuthenticationPage,
  ClaimsPage,
  ConfirmEmailPage,
  ConnectionPage,
  DashboardPage,
  DentalClaimDetailsPage,
  DirectMemberReimbursementPage,
  DirectMemberReimbursementSuccessPage,
  DocumentsPage,
  EligibilityHomePage,
  EmulationCallbackPage,
  FindBestPriceDrugDetailPage,
  FindBestPriceDrugNameSearchPage,
  FindBestPricePricingInfoPage,
  FindBestPriceResultsPage,
  ForceMultifactorAuthenticationPage,
  ForgotPasswordPage,
  InboxPage,
  LoginMultifactorAuthenticationPage,
  LoginPage,
  MedicalCareUsageLimitsPage,
  MedicalClaimDetailsPage,
  MedicarePrescriptionPaymentPlanPage,
  MessageDetailPage,
  NearbyPharmacyDetailPage,
  NearbyPharmacyPage,
  NearbyPharmacyResultsPage,
  PharmacyClaimDetailsPage,
  PreferencesPage,
  PrintCardPage,
  PrintMedicalClaimDetailsPage,
  PrintMedicalClaimsPage,
  PrintPharmacyClaimsPage,
  PrintPriorAuthorizationDetailsPage,
  PriorAuthorizationDetailsPage,
  PriorAuthorizationListPage,
  RegisterPage,
  SSOCallbackPage,
  UnsubscribePage,
  UpdatePasswordPage,
  VisionClaimDetailsPage,
} from "../pages";
import { enableMultifactorAuthentication } from "../utils/featureFlags";

export interface RouteProps {
  path: string;
  Component?: React.FC<{ pageTitle: string }> | React.ComponentClass;
  pageTitle?: string;
  label?: string;
  iconClassName?: string;
  inMainMenu?: boolean;
  inFooter?: boolean;
  inHamburgerMenu?: boolean;
  openInANewTab?: boolean;
  type?: string;
  routes?: Array<{}>;
  resetState?: () => object;
  showIconLogo?: boolean;
  menuSeparated?: boolean;
  analyticTag?: string;
}

interface AuthnzRouteProps {
  path: string;
  Component: React.FC<{ pageTitle: string; pageHeader: string }> | React.ComponentClass;
  pageTitle: string;
  pageHeader: string;
  exact?: boolean;
}

export const authnzRoutes: Partial<AuthnzRouteProps>[] = [
  {
    path: "/login",
    Component: LoginPage,
    pageTitle: "Login",
    pageHeader: "Member Login",
  },
  {
    path: "/register",
    Component: RegisterPage,
    pageTitle: "Validate User",
    pageHeader: "Create New Account",
  },
  {
    path: "/confirm",
    Component: ConfirmEmailPage,
    pageTitle: "Confirm User",
    pageHeader: "Create New Account",
  },
  {
    path: "/forgot-password",
    Component: ForgotPasswordPage,
    pageTitle: "Forgot password",
    pageHeader: "Forgot Password",
  },
  {
    path: "/update-password",
    Component: UpdatePasswordPage,
    pageTitle: "Forgot Password",
    pageHeader: "Forgot Password",
  },
  {
    path: "/emulation",
    Component: EmulationCallbackPage,
    pageTitle: "Emulation",
    pageHeader: "Emulation",
  },
  {
    path: "/sso/callback",
    Component: SSOCallbackPage,
    pageTitle: "Reading Response",
    pageHeader: "SSO",
  },
  {
    path: "/sso/register",
    Component: RegisterPage,
    pageTitle: "SSO Registration",
    pageHeader: "SSO Registration",
  },
  {
    path: "/unsubscribe",
    Component: UnsubscribePage,
    pageTitle: "Unsubscribe",
    pageHeader: "Unsubscribe",
  },
];

if (enableMultifactorAuthentication()) {
  /**
   * These three are splayed out into separate routes despite using
   * the same component because cognito needs to know a dedicated page
   * from which to send SMS messages.
   */
  authnzRoutes.push({
    path: "/login-totp-mfa",
    Component: LoginMultifactorAuthenticationPage,
    pageTitle: "Multifactor Authentication",
    pageHeader: "2-Step Verification",
  });
  authnzRoutes.push({
    path: "/login-sms-mfa",
    Component: LoginMultifactorAuthenticationPage,
    pageTitle: "Multifactor Authentication",
    pageHeader: "2-Step Verification",
  });
  authnzRoutes.push({
    path: "/login-select-mfa",
    Component: LoginMultifactorAuthenticationPage,
    pageTitle: "Multifactor Authentication",
    pageHeader: "2-Step Verification",
  });
}

/**
 * This is here because these pages are made to look like
 * the authnz routes, but in reality are not
 */
export const mfaRoutes: Partial<AuthnzRouteProps>[] = [];

if (enableMultifactorAuthentication()) {
  mfaRoutes.push({
    path: "/mfa-setup",
    Component: ForceMultifactorAuthenticationPage,
    pageTitle: "Multifactor Authentication",
    pageHeader: "2-Step Verification",
  });
}

export const appRoutes: Array<RouteProps> = [
  {
    path: "/",
    Component: DashboardPage,
    pageTitle: "My Rx",
  },
  {
    path: "/forms-and-documents",
    Component: DocumentsPage,
    pageTitle: "Forms and Documents",
  },
  {
    path: "/claims-history",
    Component: ClaimsPage,
    pageTitle: "Claims History",
  },
  {
    path: "/claims-history/pharmacy/:id",
    Component: PharmacyClaimDetailsPage,
    pageTitle: "Pharmacy Claims History",
  },
  {
    path: "/claims-history/medical/:id",
    Component: MedicalClaimDetailsPage,
    pageTitle: "Medical Claims History",
  },
  {
    path: "/claims-history/dental/:id",
    Component: DentalClaimDetailsPage,
    pageTitle: "Dental Claims History",
  },
  {
    path: "/claims-history/vision/:id",
    Component: VisionClaimDetailsPage,
    pageTitle: "Vision Claims History",
  },
  {
    path: "/notifications",
    Component: PreferencesPage,
    pageTitle: "Notifications",
  },
  {
    path: "/inbox",
    Component: InboxPage,
    pageTitle: "Inbox",
  },
  {
    path: "/inbox/:messageId",
    Component: MessageDetailPage,
    pageTitle: "Inbox",
  },
  {
    path: "/nearby-pharmacy",
    Component: NearbyPharmacyPage,
    pageTitle: "Nearby Pharmacies",
  },
  {
    path: "/nearby-pharmacy/compare-pharmacies",
    Component: NearbyPharmacyResultsPage,
    label: "Nearby Pharmacies",
    pageTitle: "Nearby Pharmacies - Compare",
  },
  {
    path: "/nearby-pharmacy/pharmacy-details/:pharmacy_id",
    Component: NearbyPharmacyDetailPage,
    pageTitle: "Nearby Pharmacies - Pharmacy Details",
  },
  {
    path: "/find-best-price",
    Component: FindBestPriceDrugNameSearchPage,
    pageTitle: "Find Best Price",
  },
  {
    path: "/find-best-price/:drug_id",
    Component: FindBestPriceDrugDetailPage,
    pageTitle: "Find Best Price",
  },
  {
    path: "/find-best-price/compare-prices/:drug_id",
    Component: FindBestPriceResultsPage,
    pageTitle: "Find Best Price - Comparison",
  },
  {
    path: "/find-best-price/:drug_id/pricing-info",
    Component: FindBestPricePricingInfoPage,
    pageTitle: "Find Best Price - Accepted Pharmacy",
  },
  {
    path: "/medicare-prescription-payment-plan",
    Component: MedicarePrescriptionPaymentPlanPage,
    pageTitle: "Medicare Prescription Payment Plan",
  },
  {
    path: "/direct-member-reimbursement",
    Component: DirectMemberReimbursementPage,
    pageTitle: "Submit a Reimbursement Claim",
  },
  {
    path: "/direct-member-reimbursement/success/:confirmationNumber",
    Component: DirectMemberReimbursementSuccessPage,
    pageTitle: "Direct Member Reimbursement Success",
  },
  {
    path: "/connection/:connectionId",
    Component: ConnectionPage,
    pageTitle: "Launch Connection",
  },
  {
    path: "/id_card",
    Component: PrintCardPage,
    pageTitle: "Print ID Card",
  },
  {
    path: "/print-pharmacy-claims-history",
    Component: PrintPharmacyClaimsPage,
    pageTitle: "Print Pharmacy Claims History",
  },
  {
    path: "/print-medical-claims-history",
    Component: PrintMedicalClaimsPage,
    pageTitle: "Print Medical Claims History",
  },
  {
    path: "/print-dental-claim-details",
    Component: PrintMedicalClaimDetailsPage,
    pageTitle: "Print Dental Claim Details",
  },
  {
    path: "/print-medical-claim-details",
    Component: PrintMedicalClaimDetailsPage,
    pageTitle: "Print Medical Claim Details",
  },
  {
    path: "/print-vision-claim-details",
    Component: PrintMedicalClaimDetailsPage,
    pageTitle: "Print Vision Claim Details",
  },
  {
    path: "/add-native-authentication",
    Component: AddNativeAuthenticationPage,
    pageTitle: "Add Native Authentication",
  },
  {
    path: "/prior-authorization/:priorAuthorizationId",
    Component: PriorAuthorizationDetailsPage,
    pageTitle: "Prior Authorization Details Page",
  },
  {
    path: "/prior-authorization",
    Component: PriorAuthorizationListPage,
    pageTitle: "Prior Authorization List Page",
  },
  {
    path: "/eligibility",
    Component: EligibilityHomePage,
    pageTitle: "Eligibility",
  },
  {
    path: "/print-prior-authorization-page",
    Component: PrintPriorAuthorizationDetailsPage,
    pageTitle: "Prior Authorization Details Print Page",
  },
  {
    path: "/care-usage-and-limits",
    Component: MedicalCareUsageLimitsPage,
    pageTitle: "Care Usage & Limits",
  },
];
